/**
 * CustomerHeader - Header strip for the standalone Customer Timeline route.
 *
 * Displays: avatar (initials-based), name, email, tier badge, and
 * "Open in Customer Manager" link.
 *
 * Only rendered in the standalone form (/customers/:id/timeline).
 * Not shown when embedded as a tab in 2558 Customer Manager.
 */

import { memo, type CSSProperties } from 'react'
import { ExternalLink, User } from 'lucide-react'
import { Skeleton } from '@/shared/components/ui/skeleton'
import type { CustomerProfile } from '../types'

export interface CustomerHeaderProps {
  customerId: string
  profile: CustomerProfile | null
  loading?: boolean
  error?: string | null
}

function getInitials(name?: string, email?: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.trim().slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return '??'
}

function TierBadge({ tier }: { tier?: string }) {
  if (!tier) return null
  return (
    <span style={styles.tierBadge}>
      {tier.toLowerCase()}
    </span>
  )
}

export const CustomerHeader = memo(function CustomerHeader({
  customerId,
  profile,
  loading = false,
  error = null,
}: CustomerHeaderProps) {
  if (loading) {
    return (
      <div style={styles.container}>
        <Skeleton style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
          <Skeleton style={{ width: 160, height: 14 }} />
          <Skeleton style={{ width: 220, height: 12 }} />
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.avatar, background: 'var(--bg-tertiary)', color: 'var(--text-faint)' }}>
          <User size={16} />
        </div>
        <div style={styles.meta}>
          <span style={styles.name}>
            {customerId}
          </span>
          {error && <span style={{ fontSize: '11px', color: 'var(--error)' }}>{error}</span>}
        </div>
      </div>
    )
  }

  const initials = getInitials(profile.name, profile.email)
  const displayName = profile.name ?? profile.email

  return (
    <div style={styles.container}>
      {/* Avatar */}
      <div style={styles.avatar} aria-hidden="true">
        {initials}
      </div>

      {/* Name + email */}
      <div style={styles.meta}>
        <div style={styles.nameRow}>
          <span style={styles.name}>{displayName}</span>
          {profile.name && profile.email && profile.name !== profile.email && (
            <span style={styles.email}>{profile.email}</span>
          )}
          <TierBadge tier={profile.tier} />
        </div>
        <span style={styles.customerId}>{customerId}</span>
      </div>

      {/* Open in Customer Manager link */}
      <a
        href={`/customers/${customerId}`}
        style={styles.openLink}
        title="Open in Customer Manager"
      >
        <span>Open in Customer Manager</span>
        <ExternalLink size={12} />
      </a>
    </div>
  )
})

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    flexShrink: 0,
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'var(--accent-primary)',
    color: 'white',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    flexShrink: 0,
    userSelect: 'none',
  },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  name: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    letterSpacing: '-0.005em',
  },
  email: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
  },
  customerId: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-faint)',
  },
  tierBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    background: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
    border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--accent-primary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  openLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    transition: 'all var(--transition-fast)',
    flexShrink: 0,
    cursor: 'pointer',
  },
}

export default CustomerHeader
